/* CODEX Access Manager (admin/mentor only)
   Search any user, then Grant or Revoke their CODEX access.
   Shows: live eligibility, override flags, simple audit log per user.
*/
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, UserCheck, UserX, ShieldCheck, ShieldX, Clock, Crown, Mail, X } from 'lucide-react';
import { codexApi } from '../api';

function StatusPill({ user }) {
  if (user.manually_revoked) {
    return (
      <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.2em', padding: '3px 8px', borderRadius: 999, background: 'rgba(239,68,68,0.18)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' }}>
        REVOKED
      </span>
    );
  }
  if (user.manually_granted) {
    return (
      <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.2em', padding: '3px 8px', borderRadius: 999, background: 'rgba(168,85,247,0.18)', color: '#c4b5fd', border: '1px solid rgba(168,85,247,0.4)' }}>
        MANUALLY ADDED
      </span>
    );
  }
  if (user.is_enrolled) {
    return (
      <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.2em', padding: '3px 8px', borderRadius: 999, background: 'rgba(212,175,55,0.18)', color: 'var(--codex-gold-light)', border: '1px solid rgba(212,175,55,0.4)' }}>
        ENROLLED
      </span>
    );
  }
  return (
    <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.2em', padding: '3px 8px', borderRadius: 999, background: 'rgba(100,100,120,0.2)', color: 'rgba(245,217,124,0.55)', border: '1px solid rgba(100,100,120,0.3)' }}>
      NOT ELIGIBLE
    </span>
  );
}

function ReasonModal({ open, mode, userName, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      data-testid="codex-access-reason-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(11,10,22,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="codex-card"
        style={{ maxWidth: 420, width: '100%', padding: 26, border: '2px solid rgba(212,175,55,0.4)', background: 'linear-gradient(180deg, #1a0e2c, #0b0a16)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="codex-eyebrow" style={{ color: mode === 'grant' ? 'var(--codex-gold)' : '#fca5a5' }}>
              {mode === 'grant' ? 'GRANT ACCESS' : 'REVOKE ACCESS'}
            </div>
            <h3 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginTop: 4 }}>
              {mode === 'grant' ? 'Welcome' : 'Pause'} {userName}
            </h3>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fdf6e3', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'rgba(253,246,227,0.75)', marginBottom: 12, lineHeight: 1.5 }}>
          {mode === 'grant'
            ? 'They will gain immediate access to the CODEX, even without a mentorship enrollment.'
            : 'They will be kicked out of the CODEX immediately. They keep their progress and can be reinstated later.'}
        </p>

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Reason (optional)</label>
        <textarea
          className="codex-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={280}
          placeholder={mode === 'grant' ? "e.g. Special early access" : "e.g. Misuse / mentor pause"}
          data-testid="codex-access-reason-input"
          style={{ marginBottom: 14, resize: 'vertical' }}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="codex-btn codex-btn-ghost" disabled={busy}>Cancel</button>
          <button
            type="submit"
            className={mode === 'grant' ? 'codex-btn codex-btn-primary' : 'codex-btn'}
            data-testid="codex-access-reason-submit"
            disabled={busy}
            style={mode === 'revoke' ? { background: 'rgba(239,68,68,0.18)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' } : undefined}
          >
            {mode === 'grant' ? <><UserCheck size={14} /> Grant access</> : <><UserX size={14} /> Revoke access</>}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function CodexAccessManager() {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // {mode, user}

  const search = async (term = q) => {
    setLoading(true);
    try {
      const d = await codexApi.accessSearch(term || '');
      setData(d);
      setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { search(''); }, []);

  const onGrant = (u) => setConfirm({ mode: 'grant', user: u });
  const onRevoke = (u) => setConfirm({ mode: 'revoke', user: u });

  const handleSubmit = async (reason) => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.mode === 'grant') await codexApi.accessGrant(confirm.user.id, reason);
      else await codexApi.accessRevoke(confirm.user.id, reason);
      setConfirm(null);
      await search(q);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="codex-access-manager">
      <div style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow"><ShieldCheck size={11} style={{ verticalAlign: '-1px' }} /> CODEX ACCESS</div>
        <h1 className="codex-h1" style={{ marginBottom: 8 }}>Manage Access to the Scrolls</h1>
        <p style={{ color: 'rgba(253,246,227,0.75)', maxWidth: 640 }}>
          Search anyone in the academy. Grant CODEX access to non-enrolled users (special invites), or revoke access from anyone misusing the game. Staff (admin/mentor) accounts cannot be revoked.
        </p>
      </div>

      <div className="codex-card" style={{ padding: 14, marginBottom: 18 }} data-testid="codex-access-search">
        <form onSubmit={(e) => { e.preventDefault(); search(); }} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Search size={16} color="var(--codex-gold-light)" style={{ flexShrink: 0 }} />
          <input
            className="codex-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="codex-access-search-input"
            style={{ flex: 1 }}
          />
          <button
            type="submit"
            className="codex-btn codex-btn-primary"
            disabled={loading}
            data-testid="codex-access-search-submit"
            style={{ padding: '8px 16px', fontSize: 12 }}
          >
            Search
          </button>
        </form>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.18)', color: '#fca5a5', marginBottom: 14, fontSize: 13 }} data-testid="codex-access-error">
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: 'rgba(253,246,227,0.6)' }}>Searching the registry…</div>
      ) : data.items.length === 0 ? (
        <div className="codex-card" style={{ padding: 30, textAlign: 'center', color: 'rgba(253,246,227,0.65)' }}>
          No users found.
        </div>
      ) : (
        <div className="codex-card" style={{ padding: 0, overflow: 'hidden' }}>
          {data.items.map((u, i) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              data-testid={`codex-access-row-${u.id}`}
              style={{
                padding: '14px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                borderBottom: i < data.items.length - 1 ? '1px solid rgba(245,217,124,0.08)' : 'none',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span className="codex-display" style={{ color: 'var(--codex-gold-light)', fontSize: 14 }}>{u.name}</span>
                  {u.role === 'mentor' && <Crown size={12} color="#c4b5fd" />}
                  <StatusPill user={u} />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(253,246,227,0.6)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Mail size={10} /> {u.email}
                  <span style={{ marginLeft: 8 }}>· tier: {u.membership_tier || '—'}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                {/* Grant */}
                {!u.eligible && u.role !== 'admin' && (
                  <button
                    type="button"
                    onClick={() => onGrant(u)}
                    disabled={busy}
                    data-testid={`codex-access-grant-${u.id}`}
                    className="codex-btn codex-btn-primary"
                    style={{ padding: '7px 12px', fontSize: 11 }}
                  >
                    <UserCheck size={11} /> Grant
                  </button>
                )}
                {/* Re-grant (re-enable revoked user) */}
                {u.manually_revoked && (
                  <button
                    type="button"
                    onClick={() => onGrant(u)}
                    disabled={busy}
                    data-testid={`codex-access-regrant-${u.id}`}
                    className="codex-btn codex-btn-ghost"
                    style={{ padding: '7px 12px', fontSize: 11 }}
                  >
                    <UserCheck size={11} /> Re-allow
                  </button>
                )}
                {/* Revoke */}
                {u.eligible && u.role !== 'admin' && u.role !== 'mentor' && (
                  <button
                    type="button"
                    onClick={() => onRevoke(u)}
                    disabled={busy}
                    data-testid={`codex-access-revoke-${u.id}`}
                    className="codex-btn"
                    style={{
                      padding: '7px 12px', fontSize: 11,
                      background: 'rgba(239,68,68,0.12)', color: '#fca5a5',
                      border: '1px solid rgba(239,68,68,0.35)',
                    }}
                  >
                    <UserX size={11} /> Revoke
                  </button>
                )}
                {(u.role === 'admin' || u.role === 'mentor') && (
                  <span style={{ fontSize: 10, color: 'rgba(253,246,227,0.4)', alignSelf: 'center', padding: '0 8px' }}>
                    <ShieldX size={10} style={{ verticalAlign: '-1px' }} /> staff — protected
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {confirm && (
          <ReasonModal
            open={!!confirm}
            mode={confirm.mode}
            userName={confirm.user.name}
            onClose={() => setConfirm(null)}
            onSubmit={handleSubmit}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
